/**
 * Section Intelligence Data
 * 
 * Rich content for each signal category including:
 * - Expanded descriptions
 * - Dynamic snapshot generators
 * - Watch items
 * - Historical context
 * - Thematic styling
 */

export const SECTION_INTELLIGENCE = {
  liquidity: {
    // Basic info
    name: 'Liquidity',
    icon: '💧',
    color: 'var(--tg-liquidity-cyan)',
    accentGradient: 'linear-gradient(135deg, rgba(0, 238, 229, 0.15) 0%, rgba(0, 238, 229, 0.02) 100%)',
    glowColor: 'rgba(0, 238, 229, 0.4)',
    
    // Expanded tagline
    tagline: 'The lifeblood of risk assets',
    
    // Rich description
    description: `When the Fed's balance sheet expands, dollars flood the system and risk assets thrive. When it contracts, assets suffocate. These signals track the plumbing that moves markets before prices do.`,
    
    // What to watch - key thresholds and upcoming events
    watchItems: [
      { text: 'TGA rebuild expected in Q1 — could drain ~$200B from system', priority: 'high' },
      { text: 'Debt ceiling dynamics may force Treasury drawdown', priority: 'medium' },
      { text: 'RRP facility usage trending toward zero — liquidity normalizing', priority: 'low' },
    ],
    
    // Historical context template
    historicalContext: {
      peakDate: 'Nov 2021',
      peakValue: '$6.4T',
      troughDate: 'Mar 2020',
      troughValue: '$3.8T',
      narrative: 'Current levels sit between the COVID-era lows and the 2021 peak that preceded the bear market.',
    },
    
    // Snapshot generator - takes signals and returns market read
    generateSnapshot: (signals) => {
      const netLiq = signals.find(s => s.signal_id === 'NET_LIQUIDITY');
      const regime = signals.find(s => s.signal_id === 'LIQUIDITY_REGIME');
      
      if (!netLiq) return 'Awaiting liquidity data...';
      
      const value = netLiq.current?.value;
      const level = netLiq.current?.level || 'normal';
      const regimeState = regime?.current?.value || regime?.current?.level || 'neutral';
      
      const formatted = typeof value === 'number' ? `$${(value / 1e6).toFixed(2)}T` : 'N/A';
      
      const levelTexts = {
        low: `Net liquidity at ${formatted} is running thin. Risk assets face headwinds as dollar scarcity intensifies.`,
        normal: `Net liquidity at ${formatted} sits in the normal range. No immediate stress, but trend direction matters.`,
        elevated: `Net liquidity at ${formatted} is abundant. Excess dollars support risk-on positioning.`,
        high: `Net liquidity at ${formatted} signals potential excess. Watch for Fed tightening signals.`,
      };
      
      let snapshot = levelTexts[level] || levelTexts.normal;
      
      if (regimeState) {
        const regimeAddons = {
          tight: ' Regime is TIGHT — defensive positioning warranted.',
          neutral: ' Regime remains NEUTRAL — balanced conditions.',
          loose: ' Regime is LOOSE — supportive backdrop for risk assets.',
        };
        snapshot += regimeAddons[regimeState.toLowerCase()] || '';
      }
      
      return snapshot;
    },
  },
  
  credit: {
    name: 'Credit',
    icon: '💳',
    color: 'var(--tg-rust-red)',
    accentGradient: 'linear-gradient(135deg, rgba(178, 51, 42, 0.15) 0%, rgba(178, 51, 42, 0.02) 100%)',
    glowColor: 'rgba(178, 51, 42, 0.4)',
    
    tagline: 'Where fear meets yield',
    
    description: `Credit spreads are the market's collective judgment on default risk. When spreads widen, capital is fleeing to safety. These signals detect stress in corporate bond markets before equity investors notice.`,
    
    watchItems: [
      { text: 'CCC spreads widening faster than B-rated — quality divergence emerging', priority: 'high' },
      { text: 'HY issuance pace slowing — refinancing wall approaching', priority: 'medium' },
      { text: 'Fallen angel risk elevated in BBB energy sector', priority: 'medium' },
    ],
    
    historicalContext: {
      peakDate: 'Mar 2020',
      peakValue: '10.8%',
      troughDate: 'Jun 2021',
      troughValue: '2.6%',
      narrative: 'HY spreads remain well below crisis levels but have drifted wider from the post-COVID lows.',
    },
    
    generateSnapshot: (signals) => {
      const hyIg = signals.find(s => s.signal_id === 'HY_IG_SPREAD');
      const cccB = signals.find(s => s.signal_id === 'CCC_B_SPREAD');
      const stressIdx = signals.find(s => s.signal_id === 'CREDIT_STRESS_INDEX');
      
      if (!hyIg) return 'Awaiting credit spread data...';
      
      const spreadVal = hyIg.current?.value;
      const spread = typeof spreadVal === 'number' ? spreadVal.toFixed(2) : 'N/A';
      const level = hyIg.current?.level || 'normal';
      
      const levelTexts = {
        low: `HY-IG spread at ${spread}% reflects complacency. Credit markets pricing minimal default risk.`,
        normal: `HY-IG spread at ${spread}% suggests measured caution. Credit risk is being priced but not feared.`,
        elevated: `HY-IG spread at ${spread}% shows stress emerging. Watch for acceleration above 4%.`,
        high: `HY-IG spread at ${spread}% signals significant stress. Default expectations rising rapidly.`,
        extreme: `HY-IG spread at ${spread}% indicates crisis conditions. Capital preservation mode.`,
      };
      
      let snapshot = levelTexts[level] || levelTexts.normal;
      
      // Add CCC context if diverging
      if (cccB?.current?.level === 'elevated' || cccB?.current?.level === 'high') {
        snapshot += ' Lowest-quality credits showing acute stress.';
      }
      
      return snapshot;
    },
  },
  
  rates: {
    name: 'Rates',
    icon: '📈',
    color: 'var(--tg-lantern-gold)',
    accentGradient: 'linear-gradient(135deg, rgba(212, 168, 95, 0.15) 0%, rgba(212, 168, 95, 0.02) 100%)',
    glowColor: 'rgba(212, 168, 95, 0.4)',
    
    tagline: 'The shape of expectations',
    
    description: `The yield curve is the bond market's crystal ball. Inversions have preceded every recession since 1955. These signals decode what rates traders are betting on — growth, inflation, and Fed policy.`,
    
    watchItems: [
      { text: 'If 2s10s re-inverts, recession probability clock restarts', priority: 'high' },
      { text: 'Real rates above 2% historically pressure equity valuations', priority: 'medium' },
      { text: 'Term premium rising — duration risk being repriced', priority: 'low' },
    ],
    
    historicalContext: {
      peakDate: 'Jul 2022',
      peakValue: '-0.50%',
      troughDate: 'Mar 2021',
      troughValue: '+1.60%',
      narrative: 'The 2s10s spread has normalized after the deepest inversion since the 1980s.',
    },
    
    generateSnapshot: (signals) => {
      const curve2s10s = signals.find(s => s.signal_id === 'CURVE_2S10S');
      const real10y = signals.find(s => s.signal_id === 'REAL_10Y');
      const termPrem = signals.find(s => s.signal_id === 'TERM_PREMIUM_PROXY');
      
      if (!curve2s10s) return 'Awaiting yield curve data...';
      
      const spread = curve2s10s.current?.value;
      const spreadBps = typeof spread === 'number' ? (spread * 100).toFixed(0) : 'N/A';
      const isInverted = typeof spread === 'number' && spread < 0;
      
      let snapshot = '';
      
      if (isInverted) {
        snapshot = `Yield curve inverted at ${spreadBps}bps — recession warning active. Historically leads downturns by 12-18 months.`;
      } else if (typeof spread === 'number' && spread < 0.25) {
        snapshot = `Yield curve barely positive at ${spreadBps}bps — economy on watch. Flattening often precedes inversion.`;
      } else if (typeof spread === 'number' && spread < 0.75) {
        snapshot = `Yield curve at ${spreadBps}bps — healthy but not steep. Modest growth expectations priced in.`;
      } else if (typeof spread === 'number') {
        snapshot = `Yield curve at ${spreadBps}bps — steep curve signals growth optimism. Risk-on backdrop for banks and cyclicals.`;
      } else {
        snapshot = 'Yield curve data loading...';
      }
      
      // Add real rates context
      const realRate = real10y?.current?.value;
      if (typeof realRate === 'number') {
        if (realRate > 2) {
          snapshot += ' Real rates restrictive — headwind for valuations.';
        } else if (realRate < 0) {
          snapshot += ' Negative real rates — financial repression favors risk assets.';
        }
      }
      
      return snapshot;
    },
  },
  
  funding: {
    name: 'Funding',
    icon: '🏦',
    color: 'var(--tg-fog-green)',
    accentGradient: 'linear-gradient(135deg, rgba(151, 184, 160, 0.15) 0%, rgba(151, 184, 160, 0.02) 100%)',
    glowColor: 'rgba(151, 184, 160, 0.4)',
    
    tagline: 'The plumbing behind the curtain',
    
    description: `Repo markets and overnight funding are the circulatory system of finance. When these markets seize, everything breaks. These signals monitor the stress that most investors never see until it's too late.`,
    
    watchItems: [
      { text: 'Quarter-end repo pressure typically spikes in final week', priority: 'medium' },
      { text: 'SOFR-EFFR divergence signals reserve distribution issues', priority: 'high' },
      { text: 'Fed standing repo facility provides backstop at 5.50%', priority: 'low' },
    ],
    
    historicalContext: {
      peakDate: 'Sep 2019',
      peakValue: '+300bps',
      troughDate: 'Normal',
      troughValue: '0-5bps',
      narrative: 'The Sep 2019 repo crisis forced emergency Fed intervention. Current spreads show no similar stress.',
    },
    
    generateSnapshot: (signals) => {
      const sofrEffr = signals.find(s => s.signal_id === 'SOFR_EFFR_SPREAD');
      const effrIorb = signals.find(s => s.signal_id === 'EFFR_IORB_SPREAD');
      const fundingStress = signals.find(s => s.signal_id === 'FUNDING_STRESS_INDEX');
      
      if (!sofrEffr && !fundingStress) return 'Awaiting funding market data...';
      
      const stressLevel = fundingStress?.current?.level || sofrEffr?.current?.level || 'normal';
      const sofrVal = sofrEffr?.current?.value;
      const sofrSpread = typeof sofrVal === 'number' ? sofrVal.toFixed(2) : 'N/A';
      
      const levelTexts = {
        low: `Money markets calm. SOFR-EFFR spread at ${sofrSpread}bps indicates smooth Fed plumbing. No action needed.`,
        normal: `Funding conditions normal. Overnight markets functioning as expected. Reserve levels adequate.`,
        elevated: `Funding stress emerging. Spread at ${sofrSpread}bps warrants monitoring. Watch for quarter-end effects.`,
        high: `Funding stress elevated. Repo markets showing strain. Fed intervention probability rising.`,
        extreme: `Funding crisis conditions. Immediate Fed action likely. Risk-off positioning critical.`,
      };
      
      return levelTexts[stressLevel] || levelTexts.normal;
    },
  },
  
  volatility: {
    name: 'Volatility',
    icon: '⚡',
    color: '#a855f7',
    accentGradient: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(168, 85, 247, 0.02) 100%)',
    glowColor: 'rgba(168, 85, 247, 0.4)',
    
    tagline: 'Fear has a price',
    
    description: `Volatility is the market's fear gauge — but also its opportunity signal. When VIX spikes, options are expensive. When it's crushed, protection is cheap. These signals track the mood of the options market.`,
    
    watchItems: [
      { text: 'VIX below 15 rarely lasts — mean reversion odds elevated', priority: 'medium' },
      { text: 'Term structure inversion signals acute near-term fear', priority: 'high' },
      { text: 'Vol-of-vol (VVIX) elevated despite low VIX — hidden risk', priority: 'medium' },
    ],
    
    historicalContext: {
      peakDate: 'Mar 2020',
      peakValue: '82.69',
      troughDate: 'Nov 2017',
      troughValue: '9.14',
      narrative: 'VIX has a long-term mean around 19-20. Extended periods below 15 often precede volatility spikes.',
    },
    
    generateSnapshot: (signals) => {
      const vixTerm = signals.find(s => s.signal_id === 'VIX_TERM_SPREAD');
      const vixZ = signals.find(s => s.signal_id === 'VIX_ZSCORE');
      
      if (!vixTerm && !vixZ) return 'Awaiting volatility data...';
      
      const termSpread = vixTerm?.current?.value;
      const zScore = vixZ?.current?.value;
      
      let snapshot = '';
      
      // Term structure interpretation
      if (typeof termSpread === 'number') {
        if (termSpread < 0) {
          snapshot = `VIX term structure inverted — markets pricing near-term fear. Historically marks panic bottoms.`;
        } else if (termSpread > 3) {
          snapshot = `VIX in steep contango — complacency elevated. Protection is cheap but no one wants it.`;
        } else {
          snapshot = `VIX term structure normal. Options market balanced between fear and greed.`;
        }
      } else {
        snapshot = `VIX term structure loading...`;
      }
      
      // Z-score context
      if (typeof zScore === 'number') {
        if (zScore < -1) {
          snapshot += ` Vol at ${zScore.toFixed(2)} z-score — unusually low. Mean reversion risk elevated.`;
        } else if (zScore > 2) {
          snapshot += ` Vol at ${zScore.toFixed(2)} z-score — extremely elevated. Often marks capitulation.`;
        }
      }
      
      return snapshot;
    },
  },
  
  macro: {
    name: 'Macro',
    icon: '🌐',
    color: '#3b82f6',
    accentGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.02) 100%)',
    glowColor: 'rgba(59, 130, 246, 0.4)',
    
    tagline: 'The big picture distilled',
    
    description: `Composite indices that synthesize dozens of inputs into actionable signals. Financial conditions, stress indices, and regime indicators — the dashboard for macro positioning.`,
    
    watchItems: [
      { text: 'CAP Stress Index crossing +1σ historically precedes drawdowns', priority: 'high' },
      { text: 'NFCI tightening ahead of Fed — leading indicator for policy', priority: 'medium' },
      { text: 'Regime shifts often cluster — watch for confirmation across indices', priority: 'medium' },
    ],
    
    historicalContext: {
      peakDate: 'Mar 2020',
      peakValue: '+4.2σ',
      troughDate: 'Jan 2020',
      troughValue: '-0.8σ',
      narrative: 'The CAP Stress Index spiked to extreme levels during COVID but has normalized since mid-2020.',
    },
    
    generateSnapshot: (signals) => {
      const capStress = signals.find(s => s.signal_id === 'CAP_STRESS_INDEX');
      const nfci = signals.find(s => s.signal_id === 'NFCI');
      
      if (!capStress && !nfci) return 'Awaiting macro composite data...';
      
      const stressVal = capStress?.current?.value;
      
      let snapshot = '';
      
      if (typeof stressVal === 'number') {
        if (stressVal > 2) {
          snapshot = `CAP Stress Index at ${stressVal.toFixed(2)}σ — crisis-level stress. Defensive positioning warranted.`;
        } else if (stressVal > 1) {
          snapshot = `CAP Stress Index at ${stressVal.toFixed(2)}σ — elevated stress. Risk management critical.`;
        } else if (stressVal > 0) {
          snapshot = `CAP Stress Index at ${stressVal.toFixed(2)}σ — mildly stressed. Monitor for deterioration.`;
        } else {
          snapshot = `CAP Stress Index at ${stressVal.toFixed(2)}σ — below-average stress. Conditions favorable for risk.`;
        }
      } else {
        snapshot = 'Macro stress indicators loading...';
      }
      
      const nfciVal = nfci?.current?.value;
      if (typeof nfciVal === 'number') {
        if (nfciVal > 0) {
          snapshot += ' Financial conditions tightening.';
        } else {
          snapshot += ' Financial conditions accommodative.';
        }
      }
      
      return snapshot;
    },
  },
  
  fx: {
    name: 'FX',
    icon: '💱',
    color: '#14b8a6',
    accentGradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15) 0%, rgba(20, 184, 166, 0.02) 100%)',
    glowColor: 'rgba(20, 184, 166, 0.4)',
    
    tagline: 'The dollar is the story',
    
    description: `Currency markets are the largest and most liquid on Earth. Dollar strength ripples through every asset class — commodities, emerging markets, and multinational earnings. These signals track the greenback's dominance.`,
    
    watchItems: [
      { text: 'DXY above 105 historically pressures EM assets and commodities', priority: 'high' },
      { text: 'USD/JPY at multi-decade highs — BOJ intervention risk elevated', priority: 'medium' },
      { text: 'EUR/USD parity watch — energy crisis implications', priority: 'medium' },
    ],
    
    historicalContext: {
      peakDate: 'Sep 2022',
      peakValue: '114.78',
      troughDate: 'Jan 2021',
      troughValue: '89.21',
      narrative: 'The dollar index surged 25% from 2021 lows as the Fed led global tightening.',
    },
    
    generateSnapshot: (signals) => {
      const dxy = signals.find(s => s.signal_id === 'DXY_INDEX');
      const dxyZ = signals.find(s => s.signal_id === 'DXY_ZSCORE');
      
      if (!dxy && !dxyZ) return 'Awaiting FX data...';
      
      const dxyVal = dxy?.current?.value;
      const zScore = dxyZ?.current?.value;
      
      let snapshot = '';
      
      if (typeof dxyVal === 'number') {
        if (dxyVal > 105) {
          snapshot = `Dollar index at ${dxyVal.toFixed(1)} — strong dollar regime. Headwind for commodities and EM.`;
        } else if (dxyVal > 100) {
          snapshot = `Dollar index at ${dxyVal.toFixed(1)} — firm but not extreme. Neutral for risk assets.`;
        } else if (dxyVal > 95) {
          snapshot = `Dollar index at ${dxyVal.toFixed(1)} — dollar weakness emerging. Tailwind for commodities.`;
        } else {
          snapshot = `Dollar index at ${dxyVal.toFixed(1)} — weak dollar. Risk-on for global assets.`;
        }
      }
      
      if (typeof zScore === 'number' && Math.abs(zScore) > 1.5) {
        snapshot += ` Z-score at ${zScore.toFixed(2)} suggests extended positioning.`;
      }
      
      return snapshot || 'Analyzing FX conditions...';
    },
  },
  
  commodities: {
    name: 'Commodities',
    icon: '🛢️',
    color: '#f59e0b',
    accentGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.02) 100%)',
    glowColor: 'rgba(245, 158, 11, 0.4)',
    
    tagline: 'Real assets, real signals',
    
    description: `Commodities are the raw materials of the global economy. Oil prices signal growth expectations, gold prices signal fear, and the spread between them tells a story about inflation and risk appetite.`,
    
    watchItems: [
      { text: 'Oil backwardation signals tight physical supply', priority: 'high' },
      { text: 'Gold above $2000 often coincides with financial stress', priority: 'medium' },
      { text: 'Copper/gold ratio tracks growth vs. fear sentiment', priority: 'medium' },
    ],
    
    historicalContext: {
      peakDate: 'Mar 2022',
      peakValue: '$130/bbl',
      troughDate: 'Apr 2020',
      troughValue: '-$37/bbl',
      narrative: 'Oil prices have normalized from the 2022 energy crisis spike but remain above pre-COVID averages.',
    },
    
    generateSnapshot: (signals) => {
      const oilSpread = signals.find(s => s.signal_id === 'OIL_TERM_SPREAD');
      const goldZ = signals.find(s => s.signal_id === 'GOLD_ZSCORE');
      const copperGold = signals.find(s => s.signal_id === 'COPPER_GOLD_RATIO');
      
      if (!oilSpread && !goldZ && !copperGold) return 'Awaiting commodity data...';
      
      let snapshot = '';
      
      const spread = oilSpread?.current?.value;
      if (typeof spread === 'number') {
        if (spread > 0) {
          snapshot = `Oil in backwardation — physical tightness, near-term supply concerns.`;
        } else {
          snapshot = `Oil in contango — ample supply, storage filling. Bearish signal.`;
        }
      }
      
      const z = goldZ?.current?.value;
      if (typeof z === 'number') {
        if (z > 1.5) {
          snapshot += ' Gold elevated — safe haven demand strong.';
        } else if (z < -1) {
          snapshot += ' Gold depressed — risk-on sentiment dominating.';
        }
      }
      
      const level = copperGold?.current?.level;
      if (level === 'high' || level === 'elevated') {
        snapshot += ' Copper/gold ratio high — growth optimism.';
      } else if (level === 'low') {
        snapshot += ' Copper/gold ratio low — defensive positioning.';
      }
      
      return snapshot || 'Analyzing commodity signals...';
    },
  },
  
  labor: {
    name: 'Labor',
    icon: '👷',
    color: '#ec4899',
    accentGradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(236, 72, 153, 0.02) 100%)',
    glowColor: 'rgba(236, 72, 153, 0.4)',
    
    tagline: 'Jobs drive everything',
    
    description: `Employment is the ultimate lagging indicator — but initial claims are leading. When layoffs accelerate, recession risk spikes. These signals track the health of the labor market in real-time.`,
    
    watchItems: [
      { text: 'Initial claims above 300K historically signal recession onset', priority: 'high' },
      { text: 'Continuing claims trend more important than single prints', priority: 'medium' },
      { text: 'Sahm Rule triggers when 3-month average rises 0.5% from trough', priority: 'high' },
    ],
    
    historicalContext: {
      peakDate: 'Apr 2020',
      peakValue: '6.9M',
      troughDate: 'Mar 2022',
      troughValue: '166K',
      narrative: 'Initial claims have normalized from the COVID spike but are trending higher from 2022 lows.',
    },
    
    generateSnapshot: (signals) => {
      const claimsZ = signals.find(s => s.signal_id === 'CLAIMS_ZSCORE');
      const sahmRule = signals.find(s => s.signal_id === 'SAHM_RULE_INDICATOR');
      
      if (!claimsZ && !sahmRule) return 'Awaiting labor market data...';
      
      let snapshot = '';
      
      const z = claimsZ?.current?.value;
      if (typeof z === 'number') {
        if (z > 2) {
          snapshot = `Initial claims z-score at ${z.toFixed(2)} — labor market deteriorating rapidly. Recession risk elevated.`;
        } else if (z > 1) {
          snapshot = `Initial claims z-score at ${z.toFixed(2)} — softening evident. Watch for acceleration.`;
        } else if (z > 0) {
          snapshot = `Initial claims z-score at ${z.toFixed(2)} — mild uptick from lows. Normal cyclical adjustment.`;
        } else {
          snapshot = `Initial claims z-score at ${z.toFixed(2)} — labor market tight. No recession signal.`;
        }
      }
      
      const val = sahmRule?.current?.value;
      if (typeof val === 'number') {
        if (val >= 0.5) {
          snapshot += ' SAHM RULE TRIGGERED — recession indicator active.';
        } else if (val >= 0.3) {
          snapshot += ` Sahm Rule at ${(val * 100).toFixed(1)}bps — approaching trigger threshold.`;
        }
      }
      
      return snapshot || 'Analyzing labor market conditions...';
    },
  },
};

/**
 * Calculate section conviction/alignment
 * Returns percentage of signals pointing same direction
 */
export function calculateSectionConviction(signals) {
  if (!signals || signals.length === 0) return { percent: 0, label: 'No Data', aligned: 0, total: 0 };
  
  const levels = signals.map(s => s.current?.level).filter(Boolean);
  if (levels.length === 0) return { percent: 0, label: 'No Data', aligned: 0, total: 0 };
  
  // Count occurrences of each level
  const counts = {};
  levels.forEach(l => { counts[l] = (counts[l] || 0) + 1; });
  
  // Find dominant level
  const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  const alignedCount = dominant[1];
  const total = levels.length;
  const percent = Math.round((alignedCount / total) * 100);
  
  let label = 'Mixed';
  if (percent >= 80) label = 'Strong Consensus';
  else if (percent >= 60) label = 'Aligned';
  else if (percent >= 40) label = 'Mixed';
  else label = 'Divergent';
  
  return { percent, label, aligned: alignedCount, total, dominantLevel: dominant[0] };
}

/**
 * Get overall section status from signals
 */
export function getSectionStatus(signals) {
  if (!signals || signals.length === 0) return 'normal';
  
  const levels = signals.map(s => s.current?.level).filter(Boolean);
  if (levels.length === 0) return 'normal';
  
  // Priority: extreme > high > elevated > normal > low
  if (levels.includes('extreme')) return 'extreme';
  if (levels.includes('high')) return 'high';
  if (levels.includes('elevated')) return 'elevated';
  if (levels.some(l => l === 'normal' || l === 'neutral')) return 'normal';
  return 'low';
}

export default SECTION_INTELLIGENCE;
